import { useCallback, useEffect, useMemo, useState } from 'react'
import { ModelCard } from './ModelCard'
import type { ModelResponse } from '../shared/types/Model'
import type { LoginResponse } from '../shared/types/LoginResponse'
import './ModelCard.css'
import { BsChatHeart } from 'react-icons/bs'
import { useMode } from '../shared/useMode'
import { useTranslation } from 'react-i18next'
import { ChatHistoryModal } from './ChatHistoryModal'
import { useTelegram } from '../hooks/useTelegram'
import { buildModelImageGallery } from '../utils/normalize'
import { applyModelProfile } from '../utils/modelProfiles'

const realismBots: Record<number, string> = {
  1: 'sofiagleambot',
  2: 'lanagleambot',
  3: 'zaragleambot',
  4: 'miagleambot',
  5: 'ivygleambot',
  6: 'ambergleambot',
}

const roleplayBots: Partial<Record<number, string>> = {
  1: 'sofiagleamrpbot',
  2: 'lanagleamrpbot',
  3: 'zaragleamrpbot',
  4: 'miagleamrpbot',
  5: 'ivygleamrpbot',
}

export function ModelBrowser({
  models,
  loginInfo,
  onLoginInfoChange,
}: {
  models: ModelResponse[]
  loginInfo: LoginResponse | null
  onLoginInfoChange: (info: LoginResponse) => void
}) {
  const [index, setIndex] = useState(0)
  const [photoIndex, setPhotoIndex] = useState(0)
  const [controlsVisible, setControlsVisible] = useState(true)
  const [chatModalOpen, setChatModalOpen] = useState(false)
  const { openTelegramLink } = useTelegram()
  const { mode } = useMode()
  const availableModels = useMemo(
    () => models.filter((model) => (
      mode === 'realism'
        ? model.hasRealism !== false
        : Boolean(roleplayBots[model.id])
    )),
    [mode, models],
  )
  const paginateModel = useCallback((dir: number) => {
    setIndex((prev) => (prev + dir + availableModels.length) % availableModels.length)
  }, [availableModels.length])

  const { t, i18n } = useTranslation()
  const currentModel = availableModels[index]
  const displayModel = useMemo(
    () => applyModelProfile(currentModel, mode, i18n.resolvedLanguage ?? i18n.language),
    [currentModel, i18n.language, i18n.resolvedLanguage, mode],
  )
  const currentImages = useMemo(
    () => buildModelImageGallery(currentModel, mode),
    [currentModel, mode],
  )

  useEffect(() => {
    setIndex(0)
  }, [mode])

  useEffect(() => {
    setPhotoIndex(0)
  }, [mode, currentModel?.id])

  const paginatePhoto = useCallback((dir: number) => {
    if (!currentImages.length) return
    setPhotoIndex((prev) => (prev + dir + currentImages.length) % currentImages.length)
  }, [currentImages.length])

  const telegramUsernames =
    mode === 'roleplay' ? roleplayBots : realismBots
  const currentTelegramUsername = telegramUsernames[currentModel.id]

  const openBotLink = (url: string) => {
    if (window.Telegram?.WebApp?.openTelegramLink) {
      openTelegramLink(url)
      return
    }

    window.open(url, '_blank')
  }

  return (
    <div className="browser-container">
      <ModelCard
        model={currentModel}
        photoIndex={photoIndex}
        onModelSwipe={paginateModel}
        controlsVisible={controlsVisible}
        setControlsVisible={setControlsVisible}
        loginInfo={loginInfo}
        onLoginInfoChange={onLoginInfoChange}
      />
      <div className={`action-bar ${controlsVisible ? 'visible' : 'hidden'}`}>
        <div className="action-background">
          <div className="action-buttons">
          <button onClick={() => paginatePhoto(-1)} className="action-button left custom-button-left">
            <svg width="20" height="16" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight: '16px'}}>
              <path d="M7.5 0.5L1.5 6L7.5 11.5" stroke="#EFEFEF" strokeLinecap="round"/>
              <path d="M13.5 0.5L7.5 6L13.5 11.5" stroke="#EFEFEF" strokeLinecap="round"/>
            </svg>
          </button>
          <button
            className="action-button chat"
            onClick={() => {
              if (!currentTelegramUsername) return
              setChatModalOpen(true)
            }}
            disabled={!currentTelegramUsername}
          >
            <BsChatHeart size={21} className="chat-icon" />
            <span>{t('openChat')}</span>
          </button>
          <button onClick={() => paginatePhoto(1)} className="action-button right custom-button">
            <svg width="20" height="16" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginLeft: '16px'}}>
              <path d="M6.5 0.5L12.5 6L6.5 11.5" stroke="#EFEFEF" strokeLinecap="round"/>
              <path d="M0.5 0.5L6.5 6L0.5 11.5" stroke="#EFEFEF" strokeLinecap="round"/>
            </svg>
          </button>
          </div>
        </div>
      </div>
      {chatModalOpen && (
        <ChatHistoryModal
          model={displayModel}
          mode={mode}
          botUsername={currentTelegramUsername}
          onClose={() => setChatModalOpen(false)}
          onOpenTelegramLink={openBotLink}
        />
      )}
    </div>
  )
}
